import { PencilIcon, TrashIcon } from '@heroicons/react/24/outline';
import { DocumentIcon, CheckIcon, XMarkIcon } from '@heroicons/react/24/solid';
import { useState, useEffect, useRef } from 'react';

export interface DocumentFile {
  id: string;
  displayFilename: string;
  description?: string | null;
  createdAt: string;
}

export interface DocumentFileItemProps {
  file: DocumentFile;
  canEdit: boolean;
  onView: () => void;
  onDelete: () => void;
  onRename?: (fileId: string, newName: string) => Promise<void>;
  ActionsMenu: React.ComponentType<{
    actions: Array<{
      label: string;
      icon: React.ComponentType<{ className?: string }>;
      onClick: () => void;
      variant?: 'default' | 'danger';
      hidden?: boolean;
    }>;
    buttonClassName?: string;
  }>;
}

export const DocumentFileItem = ({
  file,
  canEdit,
  onView,
  onDelete,
  onRename,
  ActionsMenu,
}: DocumentFileItemProps) => {
  const [editingFileId, setEditingFileId] = useState<string | null>(null);
  const [editingName, setEditingName] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  // Focus input when editing starts
  useEffect(() => {
    if (editingFileId && inputRef.current) {
      inputRef.current.focus();
    }
  }, [editingFileId]);

  const startEditing = (fileId: string, currentName: string) => {
    setEditingFileId(fileId);
    setEditingName(currentName);
  };

  const cancelEditing = () => {
    setEditingFileId(null);
    setEditingName('');
  };

  const handleRenameFile = async (fileId: string) => {
    const trimmedName = editingName.trim();
    if (!trimmedName) {
      // Note: Consumers should handle toast notifications
      return;
    }

    if (!onRename) {
      cancelEditing();
      return;
    }

    try {
      await onRename(fileId, trimmedName);
      cancelEditing();
    } catch {
      // Keep editing mode open so user can retry
      // Consumer's onRename should handle showing error toast
    }
  };

  return (
    <div className="flex items-center justify-between">
      <div className="flex min-w-0 flex-1 items-center space-x-3">
        <DocumentIcon className="size-5 shrink-0 text-gray-400" />
        <div className="min-w-0 flex-1">
          {editingFileId === file.id ? (
            <div className="flex items-center gap-2">
              <input
                ref={inputRef}
                type="text"
                value={editingName}
                onChange={(e) => setEditingName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleRenameFile(file.id);
                  else if (e.key === 'Escape') cancelEditing();
                }}
                className="focus:border-secondaryBlue focus:ring-secondaryBlue block w-full rounded-md border-gray-300 text-sm"
              />
              <button
                type="button"
                onClick={() => handleRenameFile(file.id)}
                className="p-1 text-green-600 transition-colors hover:text-green-700"
                title="Save"
              >
                <CheckIcon className="size-4" />
              </button>
              <button
                type="button"
                onClick={cancelEditing}
                className="p-1 text-gray-400 transition-colors hover:text-gray-600"
                title="Cancel"
              >
                <XMarkIcon className="size-4" />
              </button>
            </div>
          ) : (
            <>
              <button
                type="button"
                onClick={onView}
                className="text-secondaryBlue hover:text-secondaryBlueShade truncate text-left text-sm font-semibold transition-colors hover:underline"
              >
                {file.displayFilename}
              </button>
              <div className="mt-1 text-xs text-gray-400">
                {`Uploaded on: ${new Date(file.createdAt).toLocaleDateString()}`}
              </div>
            </>
          )}
        </div>
      </div>

      {editingFileId !== file.id && (
        <ActionsMenu
          actions={[
            ...(canEdit && onRename
              ? [
                  {
                    label: 'Rename',
                    icon: PencilIcon,
                    onClick: () => startEditing(file.id, file.displayFilename),
                  },
                ]
              : []),
            {
              label: 'Delete',
              icon: TrashIcon,
              onClick: onDelete,
              variant: 'danger' as const,
              hidden: !canEdit,
            },
          ]}
          buttonClassName="rounded-md p-1.5 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
        />
      )}
    </div>
  );
};
